export const MEDICATION_CATEGORIES = [
  "Todos",
  "Antibióticos",
  "Analgésicos",
  "Anti-inflamatórios",
  "Antieméticos",
  "Antivirais",
  "Antialérgicos",
  "Outros",
];

const consultaRemedios = (slug) => `https://consultaremedios.com.br/${slug}/bula`;

const review = {
  status: "pending",
  reviewedBy: "Pendente de revisão pela Dra. Aline",
  reviewedAt: null,
};

const aapdSource = {
  label: "AAPD - Useful Medications for Oral Conditions",
  url: "https://www.aapd.org/globalassets/media/policies_guidelines/r_usefulmeds.pdf",
};

const manualRegimen = (route = "Via oral") => ({
  id: "manual",
  label: "Cálculo manual pelo profissional",
  calculation: "manual",
  route,
  durationDays: null,
  notes: "Dose, frequência, limites e duração devem ser informados pelo profissional.",
});

export const medications = [
  {
    id: "amoxicilina",
    name: "Amoxicilina",
    presentation: "Suspensão 250 mg/5 mL (50 mg/mL)",
    category: "Antibióticos",
    concentrationMgPerMl: 50,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("amoxicilina"),
    sources: [aapdSource],
    review,
    warnings: ["Confirmar ausência de alergia a penicilinas/betalactâmicos."],
    regimens: [
      {
        id: "odontogenic-q8h",
        label: "Infecção odontogênica - dividido a cada 8 horas",
        calculation: "mgKgDay",
        doseMgKg: 30,
        allowedDoseRange: [20, 40],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 7,
        maxSingleMg: 500,
        route: "Via oral",
        notes: "Faixa de 20-40 mg/kg/dia dividida a cada 8 horas.",
      },
      {
        id: "odontogenic-q12h",
        label: "Infecção odontogênica - dividido a cada 12 horas",
        calculation: "mgKgDay",
        doseMgKg: 35,
        allowedDoseRange: [25, 45],
        dosesPerDay: 2,
        intervalHours: 12,
        durationDays: 7,
        maxSingleMg: 875,
        route: "Via oral",
        notes: "Faixa de 25-45 mg/kg/dia dividida a cada 12 horas.",
      },
      {
        id: "endocarditis-prophylaxis",
        label: "Profilaxia de endocardite - dose única",
        calculation: "mgKgDose",
        doseMgKg: 50,
        dosesPerDay: 1,
        intervalHours: null,
        durationDays: 1,
        maxSingleMg: 2000,
        route: "Via oral",
        notes: "Administrar 30-60 minutos antes do procedimento, quando indicado.",
      },
      manualRegimen(),
    ],
  },
  {
    id: "amoxicilina-clavulanato",
    name: "Amoxicilina + Clavulanato",
    presentation: "Suspensão variável - informar concentração do componente amoxicilina",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("amoxicilina-clavulanato-de-potassio"),
    sources: [aapdSource],
    review,
    warnings: [
      "O cálculo usa exclusivamente o componente amoxicilina.",
      "Confirmar ausência de alergia a penicilinas/betalactâmicos.",
    ],
    regimens: [
      {
        id: "odontogenic-q12h",
        label: "Infecção odontogênica - dividido a cada 12 horas",
        calculation: "mgKgDay",
        doseMgKg: 35,
        allowedDoseRange: [25, 45],
        dosesPerDay: 2,
        intervalHours: 12,
        durationDays: 7,
        maxSingleMg: 875,
        maxDailyMg: 1750,
        route: "Via oral",
        notes: "Dose calculada pelo componente amoxicilina.",
      },
      manualRegimen(),
    ],
  },
  {
    id: "azitromicina",
    name: "Azitromicina",
    presentation: "Suspensão 200 mg/5 mL (40 mg/mL)",
    category: "Antibióticos",
    concentrationMgPerMl: 40,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("azitromicina"),
    sources: [aapdSource],
    review,
    warnings: ["Pode causar arritmias em pacientes com defeitos de condução cardíaca."],
    regimens: [
      {
        id: "odontogenic",
        label: "Regime odontológico - uma vez ao dia",
        calculation: "mgKgDose",
        doseMgKg: 10,
        allowedDoseRange: [10, 12],
        dosesPerDay: 1,
        intervalHours: 24,
        durationDays: 3,
        maxSingleMg: 500,
        route: "Via oral",
        notes: "A dose pode variar conforme a indicação e o dia do tratamento.",
      },
      manualRegimen(),
    ],
  },
  {
    id: "claritromicina",
    name: "Claritromicina",
    presentation: "Suspensão 125 mg/5 mL ou 250 mg/5 mL",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("claritromicina"),
    sources: [aapdSource],
    review,
    warnings: ["Pode causar arritmias em pacientes com defeitos de condução cardíaca."],
    regimens: [
      {
        id: "odontogenic",
        label: "Regime odontológico - a cada 12 horas",
        calculation: "mgKgDose",
        doseMgKg: 7.5,
        dosesPerDay: 2,
        intervalHours: 12,
        durationDays: 7,
        maxSingleMg: 500,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "cefalexina",
    name: "Cefalexina",
    presentation: "Suspensão variável - informar mg/mL",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("cefalexina"),
    sources: [aapdSource],
    review,
    warnings: ["Evitar em histórico de anafilaxia, angioedema ou urticária com penicilina/ampicilina."],
    regimens: [
      {
        id: "mild-moderate",
        label: "Infecção leve a moderada - dividido a cada 8 horas",
        calculation: "mgKgDay",
        doseMgKg: 30,
        allowedDoseRange: [25, 50],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 7,
        maxDailyMg: 2000,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "ceftriaxona",
    name: "Ceftriaxona",
    presentation: "Pó liofilizado IM/IV (250 mg, 500 mg, 1 g)",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "IM/IV",
    documentType: "clinical-summary",
    leafletUrl: consultaRemedios("ceftriaxona"),
    sources: [],
    review,
    warnings: ["Uso injetável: confirmar indicação, reconstituição, diluição e administração."],
    regimens: [manualRegimen("IM/IV")],
  },
  {
    id: "clindamicina",
    name: "Clindamicina",
    presentation: "Suspensão variável - informar mg/mL",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("clindamicina"),
    sources: [aapdSource],
    review,
    warnings: ["Risco de colite associada a Clostridioides difficile."],
    regimens: [
      {
        id: "soft-tissue",
        label: "Infecção de tecidos moles - dividido a cada 8 horas",
        calculation: "mgKgDay",
        doseMgKg: 25,
        allowedDoseRange: [20, 30],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 7,
        maxSingleMg: 450,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "metronidazol",
    name: "Metronidazol",
    presentation: "Suspensão 200 mg/5 mL (40 mg/mL) - comum",
    category: "Antibióticos",
    concentrationMgPerMl: 40,
    route: "Via oral",
    documentType: "antibiotic",
    leafletUrl: consultaRemedios("metronidazol"),
    sources: [aapdSource],
    review,
    warnings: ["Evitar álcool durante o tratamento."],
    regimens: [
      {
        id: "periodontal-allergy",
        label: "Doença periodontal em alergia a penicilina",
        calculation: "mgKgDose",
        doseMgKg: 10,
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 7,
        maxSingleMg: 250,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "penicilina-g-benzatina",
    name: "Penicilina G Benzatina",
    presentation: "Pó liofilizado IM (600.000 UI ou 1.200.000 UI)",
    category: "Antibióticos",
    concentrationMgPerMl: null,
    route: "IM",
    documentType: "clinical-summary",
    leafletUrl: consultaRemedios("benzilpenicilina-benzatina"),
    sources: [],
    review,
    warnings: ["Uso injetável: confirmar alergia, indicação e técnica de administração."],
    regimens: [manualRegimen("IM")],
  },
  {
    id: "dipirona-gotas",
    name: "Dipirona Sódica (gotas)",
    presentation: "Solução oral 500 mg/mL",
    category: "Analgésicos",
    concentrationMgPerMl: 500,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("dipirona-sodica"),
    sources: [],
    review,
    warnings: ["Conversão em gotas indisponível até validar o fator gotas/mL da apresentação."],
    regimens: [manualRegimen()],
  },
  {
    id: "tramadol-gotas",
    name: "Tramadol (gotas)",
    presentation: "Solução oral 100 mg/mL",
    category: "Analgésicos",
    concentrationMgPerMl: 100,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "clinical-summary",
    leafletUrl: consultaRemedios("cloridrato-de-tramadol"),
    sources: [],
    review,
    warnings: ["Medicamento sujeito a controle especial. O resumo não substitui o receituário exigido."],
    regimens: [manualRegimen()],
  },
  {
    id: "paracetamol-suspensao",
    name: "Paracetamol",
    presentation: "Suspensão oral 32 mg/mL (160 mg/5 mL)",
    category: "Analgésicos",
    concentrationMgPerMl: 32,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("paracetamol"),
    sources: [aapdSource],
    review,
    warnings: ["Doses agudas e crônicas excessivas estão associadas a hepatotoxicidade."],
    regimens: [
      {
        id: "pain",
        label: "Dor leve a moderada - a cada 6 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 10,
        allowedDoseRange: [10, 15],
        dosesPerDay: 4,
        intervalHours: 6,
        durationDays: 3,
        maxSingleMg: 1000,
        maxDailyMgKg: 75,
        maxDailyMg: 4000,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "paracetamol-gotas",
    name: "Paracetamol (gotas)",
    presentation: "Solução oral (gotas) 200 mg/mL",
    category: "Analgésicos",
    concentrationMgPerMl: 200,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("paracetamol"),
    sources: [aapdSource],
    review,
    warnings: [
      "Conversão em gotas indisponível até validar o fator gotas/mL da apresentação.",
      "Doses excessivas estão associadas a hepatotoxicidade.",
    ],
    regimens: [
      {
        id: "pain",
        label: "Dor leve a moderada - a cada 6 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 10,
        allowedDoseRange: [10, 15],
        dosesPerDay: 4,
        intervalHours: 6,
        durationDays: 3,
        maxSingleMg: 1000,
        maxDailyMgKg: 75,
        maxDailyMg: 4000,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "nimesulida-gotas",
    name: "Nimesulida (gotas)",
    presentation: "Solução oral 50 mg/mL",
    category: "Anti-inflamatórios",
    concentrationMgPerMl: 50,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("nimesulida"),
    sources: [],
    review,
    warnings: ["Contraindicada para menores de 12 anos. Validar risco hepático e duração."],
    restrictions: { minAgeYears: 12 },
    regimens: [manualRegimen()],
  },
  {
    id: "ibuprofeno-suspensao",
    name: "Ibuprofeno (suspensão)",
    presentation: "Suspensão oral 20 mg/mL (100 mg/5 mL)",
    category: "Anti-inflamatórios",
    concentrationMgPerMl: 20,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("ibuprofeno"),
    sources: [aapdSource],
    review,
    warnings: ["Avaliar alergia a AINEs, doença renal e risco gastrointestinal."],
    regimens: [
      {
        id: "pain",
        label: "Dor leve a moderada - a cada 6-8 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 5,
        allowedDoseRange: [4, 10],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 3,
        maxSingleMg: 600,
        maxDailyMg: 3200,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "ibuprofeno-gotas-50",
    name: "Ibuprofeno (gotas 50 mg/mL)",
    presentation: "Suspensão gotas 50 mg/mL",
    category: "Anti-inflamatórios",
    concentrationMgPerMl: 50,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("ibuprofeno"),
    sources: [aapdSource],
    review,
    warnings: ["Conversão em gotas indisponível até validar o fator gotas/mL da apresentação."],
    regimens: [
      {
        id: "pain",
        label: "Dor leve a moderada - a cada 8 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 5,
        allowedDoseRange: [4, 10],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 3,
        maxSingleMg: 600,
        maxDailyMg: 3200,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "ibuprofeno-gotas-100",
    name: "Ibuprofeno (gotas 100 mg/mL)",
    presentation: "Suspensão gotas 100 mg/mL",
    category: "Anti-inflamatórios",
    concentrationMgPerMl: 100,
    dropsPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("ibuprofeno"),
    sources: [aapdSource],
    review,
    warnings: ["Conversão em gotas indisponível até validar o fator gotas/mL da apresentação."],
    regimens: [
      {
        id: "pain",
        label: "Dor leve a moderada - a cada 8 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 5,
        allowedDoseRange: [4, 10],
        dosesPerDay: 3,
        intervalHours: 8,
        durationDays: 3,
        maxSingleMg: 600,
        maxDailyMg: 3200,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  {
    id: "naproxeno",
    name: "Naproxeno",
    presentation: "Suspensão/tablete - informar mg/mL se suspensão",
    category: "Anti-inflamatórios",
    concentrationMgPerMl: null,
    route: "Via oral",
    documentType: "standard",
    leafletUrl: consultaRemedios("naproxeno"),
    sources: [aapdSource],
    review,
    warnings: ["Avaliar alergia a AINEs, doença renal e risco gastrointestinal."],
    regimens: [
      {
        id: "pain",
        label: "Dor - a cada 12 horas se necessário",
        calculation: "mgKgDose",
        doseMgKg: 5,
        allowedDoseRange: [5, 7],
        dosesPerDay: 2,
        intervalHours: 12,
        durationDays: 3,
        maxDailyMg: 1000,
        route: "Via oral",
      },
      manualRegimen(),
    ],
  },
  ...[
    ["ondansetrona", "Ondansetrona", "Solução oral 4 mg/5 mL (0,8 mg/mL)", "Antieméticos", 0.8, null, "ondansetrona"],
    ["dramin-b6-gotas", "Dramin B6 (gotas)", "Solução B6 gotas 25 mg/mL (1,25 mg/gota)", "Antieméticos", 25, 20, "dimenidrinato-cloridrato-de-piridoxina"],
    ["bromoprida", "Bromoprida", "Solução gotas 4 mg/mL", "Antieméticos", 4, null, "bromoprida"],
    ["metoclopramida", "Metoclopramida", "Solução gotas 4 mg/mL", "Antieméticos", 4, null, "cloridrato-de-metoclopramida"],
    ["aciclovir", "Aciclovir", "Comprimido 200 mg", "Antivirais", null, null, "aciclovir"],
    ["prednisolona", "Prednisolona", "Xarope 3 mg/mL (15 mg/5 mL)", "Anti-inflamatórios", 3, null, "prednisolona"],
    ["dexametasona-elixir", "Dexametasona (elixir)", "Elixir 0,1 mg/mL", "Anti-inflamatórios", 0.1, null, "dexametasona"],
    ["betametasona-elixir", "Betametasona (elixir)", "Elixir 0,1 mg/mL", "Anti-inflamatórios", 0.1, null, "betametasona"],
    ["prednisona", "Prednisona", "Solução oral 3 mg/mL ou comprimido", "Anti-inflamatórios", 3, null, "prednisona"],
    ["loratadina", "Loratadina", "Xarope 1 mg/mL", "Antialérgicos", 1, null, "loratadina"],
    ["desloratadina", "Desloratadina", "Xarope 0,5 mg/mL", "Antialérgicos", 0.5, null, "desloratadina"],
    ["cetirizina", "Cetirizina", "Solução oral 1 mg/mL", "Antialérgicos", 1, null, "dicloridrato-de-cetirizina"],
    ["hidroxizina", "Hidroxizina", "Solução oral 2 mg/mL", "Antialérgicos", 2, null, "cloridrato-de-hidroxizina"],
    ["dexclorfeniramina", "Dexclorfeniramina", "Xarope 0,4 mg/mL", "Antialérgicos", 0.4, null, "maleato-de-dexclorfeniramina"],
    ["prometazina", "Prometazina", "Comprimido 25 mg / injetável", "Antialérgicos", null, null, "cloridrato-de-prometazina"],
    ["clorexidina", "Clorexidina 0,12%", "Solução dental 0,12% (uso enxágue)", "Outros", null, null, "digliconato-de-clorexidina"],
  ].map(([id, name, presentation, category, concentrationMgPerMl, dropsPerMl, slug]) => ({
    id,
    name,
    presentation,
    category,
    concentrationMgPerMl,
    dropsPerMl,
    route: id === "clorexidina" ? "Enxágue bucal" : "Via oral",
    documentType: id === "prometazina" ? "clinical-summary" : "standard",
    leafletUrl: consultaRemedios(slug),
    sources: [],
    review,
    warnings: dropsPerMl
      ? ["Fator de gotas cadastrado como referência e pendente de validação clínica."]
      : ["Regime automático indisponível até revisão clínica."],
    regimens: [manualRegimen(id === "clorexidina" ? "Enxágue bucal" : "Via oral")],
  })),
];

export const medicationById = Object.fromEntries(
  medications.map((medication) => [medication.id, medication])
);
